import { OPERATOR_GT_EQUALS, OPERATOR_LT_EQUALS } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { JqlData, JqlInfo } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import type { TypeConfig, DateTimeValue } from '../../common/types.tsx';
import messages from './messages.tsx';
import { isValidTimePeriodFormat } from './time-functions.tsx';

export const DUE_IN_NEXT_CONFIG: TypeConfig = {
	type: 'due_in_next',
	componentType: 'time_input',
	label: 'labelDueInNext',
	hasInput: true,
	getErrorMessage: (intl: IntlShape, value?: DateTimeValue): string | undefined =>
		isValidTimePeriodFormat(value?.value1, value?.value2)
			? undefined
			: intl.formatMessage(messages.validationMessageInvalidPeriodFormat),
	getFormattedValue: (intl: IntlShape, value?: DateTimeValue) => {
		if (value !== undefined) {
			const { excludeOverdue = false } = value;
			switch (value.value2.toLowerCase()) {
				case 'm':
					return excludeOverdue
						? intl.formatMessage(messages.valueDueInNextExcludeOverdueMinutes, {
								dateNumber: value.value1,
							})
						: intl.formatMessage(messages.valueDueInNextMinutes, {
								dateNumber: value.value1,
							});
				case 'h':
					return excludeOverdue
						? intl.formatMessage(messages.valueDueInNextExcludeOverdueHours, {
								dateNumber: value.value1,
							})
						: intl.formatMessage(messages.valueDueInNextHours, {
								dateNumber: value.value1,
							});
				case 'd':
					return excludeOverdue
						? intl.formatMessage(messages.valueDueInNextExcludeOverdueDays, {
								dateNumber: value.value1,
							})
						: intl.formatMessage(messages.valueDueInNextDays, {
								dateNumber: value.value1,
							});
				case 'w':
					return excludeOverdue
						? intl.formatMessage(messages.valueDueInNextExcludeOverdueWeeks, {
								dateNumber: value.value1,
							})
						: intl.formatMessage(messages.valueDueInNextWeeks, {
								dateNumber: value.value1,
							});
				default:
					return value.value2;
			}
		}
		return '';
	},
	getJqlData: (dateTimeValue?: DateTimeValue) => {
		if (!dateTimeValue) {
			return undefined;
		}
		const { value1, value2, excludeOverdue } = dateTimeValue;

		return excludeOverdue === true
			? {
					primaryClause: {
						operator: OPERATOR_GT_EQUALS,
						operand: '0',
					},
					secondaryClause: {
						operator: OPERATOR_LT_EQUALS,
						operand: `${value1}${value2}`,
					},
				}
			: {
					primaryClause: {
						operator: OPERATOR_LT_EQUALS,
						operand: `${value1}${value2}`,
					},
				};
	},
	getValueFromJqlData: (jqlData?: JqlData): DateTimeValue | undefined => {
		if (jqlData !== undefined) {
			const { primaryClause, secondaryClause } = jqlData;
			// if both clauses are present check value of operand if it's '0'
			if (primaryClause === undefined && secondaryClause === undefined) {
				// Does not match type if more than one clause is present
				return undefined;
			}
			if (primaryClause !== undefined && secondaryClause !== undefined) {
				const returnValue = { value1: '', value2: '', excludeOverdue: false };
				[primaryClause, secondaryClause].forEach((clause) => {
					const operatorValue = clause.operator ? clause.operator : '';
					const value = clause.operand !== undefined ? clause.operand : '';
					switch (operatorValue) {
						case OPERATOR_GT_EQUALS: {
							if (value === '0' || value === '"0"') {
								returnValue.excludeOverdue = true;
							}
							break;
						}
						case OPERATOR_LT_EQUALS:
							if (value) {
								const matches = value.match(/^([0-9]*)([mhdw])$/);
								if (matches?.length === 3) {
									const [, value1, value2] = matches;
									returnValue.value1 = value1;
									returnValue.value2 = value2;
								}
							}
							break;
						default:
							break;
					}
				});
				if (returnValue.value1 && returnValue.value2 && returnValue.excludeOverdue) {
					return returnValue;
				}
			}
			if (
				(primaryClause === undefined && secondaryClause !== undefined) ||
				(primaryClause !== undefined && secondaryClause === undefined)
			) {
				const clause: JqlInfo | undefined = primaryClause || secondaryClause;
				if (clause && clause.operator === OPERATOR_LT_EQUALS) {
					const value = clause.operand !== undefined ? clause.operand : '';
					if (value !== undefined) {
						const matches = value.match(/^([0-9]*)([mhdw])$/);
						if (matches?.length === 3) {
							return {
								value1: matches[1],
								value2: matches[2],
							};
						}
					}
				}
			}
			return undefined;
		}
		return undefined;
	},
	overduePicker: true,
};
